/**
 * Shared logout coordination module.
 *
 * All client-side code that needs to detect or trigger a logout should use the
 * functions exported here instead of maintaining its own `isLoggingOut` flag.
 * This prevents multiple modules from racing to clear the session and redirect
 * independently.
 */
import { broadcast, subscribe } from "@/utils/common/crossTabChannel";

/** DOM event dispatched synchronously before logout cleanup or navigation. */
export const LOGOUT_STARTED_EVENT = "gail:logout-started";

/**
 * DOM event name dispatched when the session is irrecoverably expired.
 * Imported by SessionTimeoutProvider and tests.
 */
export const SESSION_EXPIRED_EVENT = "gail:session-expired";

/**
 * DOM event name dispatched after a successful token refresh.
 * AuthProvider listens and re-runs `buildAuthSeed()` via `router.refresh()`.
 */
export const TOKEN_REFRESHED_EVENT = "gail:token-refreshed";

export function notifyTokenRefreshed(): void {
  if (globalThis.window === undefined) return;
  globalThis.window.dispatchEvent(new CustomEvent(TOKEN_REFRESHED_EVENT));
}

// Use the existing loginLocation key that the springboard already reads.
// This matches SetLoginLocation from @/utils/common/login.ts
const LOGIN_LOCATION_KEY = "loginLocation";

// Sibling-tab broadcast so other tabs skip their doomed /api/auth/refresh
// once one tab has seen the refresh token rejected.
const SESSION_EXPIRED_CHANNEL = "sessionExpiredAt";

let _isLoggingOut = false;
let _sessionExpiredNotified = false;
let _sessionExpiredResetTimer: ReturnType<typeof setTimeout> | null = null;
let _hasSessionExpiredListener = false;
// Persistent latch: set the first time notifySessionExpired runs and only
// cleared by _resetLogoutGuard() (tests) or module reload after the
// /auth/logout navigation — clearCachesAndLogout intentionally leaves it set
// so the latch survives the no-listener fallback path until the hard nav
// re-evaluates this module. Distinct from _sessionExpiredNotified, which
// gates the modal re-fire on a 30s cooldown — re-firing the modal is
// desirable, re-attempting a doomed /api/auth/refresh against an
// already-rotated refresh token is not.
let _sessionExpired = false;

// After this many milliseconds the _sessionExpiredNotified flag auto-resets so
// that a subsequent genuine 401 can re-trigger the modal / fallback logout.
export const SESSION_EXPIRED_NOTIFY_COOLDOWN_MS = 30_000;

/** Mark logout before cleanup or navigation so realtime transports stop immediately. */
export function notifyLogoutStarted(): void {
  if (_isLoggingOut) return;
  _isLoggingOut = true;
  if (globalThis.window !== undefined) {
    globalThis.window.dispatchEvent(new CustomEvent(LOGOUT_STARTED_EVENT));
  }
}

/**
 * Returns whether a logout redirect is already in progress.
 */
export function isLoggingOut(): boolean {
  return _isLoggingOut;
}

/**
 * Returns whether the session has been declared terminally expired (the
 * SessionExpired modal has been raised at least once since the page loaded or
 * a logout/reset ran). Refresh dispatchers consult this to short-circuit
 * doomed retries against an already-rotated refresh token.
 */
export function isSessionExpired(): boolean {
  return _sessionExpired;
}

/**
 * Resets the logout guard. Only used for testing.
 * @internal
 */
export function _resetLogoutGuard(): void {
  _isLoggingOut = false;
  _sessionExpiredNotified = false;
  _sessionExpired = false;
  if (_sessionExpiredResetTimer) {
    clearTimeout(_sessionExpiredResetTimer);
    _sessionExpiredResetTimer = null;
  }
  _hasSessionExpiredListener = false;
}

/**
 * Registers that a SessionTimeoutProvider is mounted and listening for
 * `gail:session-expired` events. If no listener is registered when
 * {@link notifySessionExpired} fires, it falls back to
 * {@link clearCachesAndLogout} directly.
 *
 * Also subscribes to sibling-tab session-expired broadcasts so a 401 in one
 * tab surfaces the modal in every open tab. Returns a cleanup function that
 * undoes both registrations.
 */
export function registerSessionExpiredListener(): () => void {
  _hasSessionExpiredListener = true;
  const unsubscribeCrossTab = subscribe(SESSION_EXPIRED_CHANNEL, () => {
    if (_sessionExpired) return;
    notifySessionExpired();
  });
  return () => {
    _hasSessionExpiredListener = false;
    unsubscribeCrossTab();
  };
}

/**
 * Clears all local caches and redirects to the Auth0 logout flow.
 *
 * This function is safe to call from multiple places concurrently — only the
 * first invocation will execute; subsequent calls while a logout is in progress
 * are no-ops.
 *
 * @param options.saveReturnUrl  When true the current URL is persisted so the
 *   user is returned to the same page after re-authenticating.
 */
export async function clearCachesAndLogout(
  options: { saveReturnUrl?: boolean } = {}
): Promise<void> {
  if (_isLoggingOut) {
    return;
  }
  notifyLogoutStarted();

  if (options.saveReturnUrl) {
    const returnTo = globalThis.location.pathname + globalThis.location.search;
    try {
      if (returnTo && returnTo.length > 1 && !returnTo.startsWith("/?")) {
        globalThis.localStorage.setItem(LOGIN_LOCATION_KEY, returnTo);
      }
    } catch {
      // localStorage might not be available
    }
  }

  try {
    const lscacheModule = await import("lscache");
    lscacheModule.default.flush();
  } catch {
    // lscache might not be available
  }

  globalThis.sessionStorage.clear();

  _sessionExpiredNotified = false;
  // Intentionally NOT clearing _sessionExpired here: notifySessionExpired's
  // no-listener fallback calls into this function, and the latch needs to
  // outlive that path until the page navigates to /auth/logout. The hard nav
  // below re-evaluates the module on the next page, which clears it.
  if (_sessionExpiredResetTimer) {
    clearTimeout(_sessionExpiredResetTimer);
    _sessionExpiredResetTimer = null;
  }

  // Do NOT call /api/auth/clear-session here. The session cookies must still
  // be present when the browser navigates to /auth/logout so the Auth0 SDK can
  // read them and include id_token_hint / logout_hint in the OIDC logout URL.
  // The proxy's /auth/logout handler and the Auth0 SDK clear the cookies on
  // the response.

  // eslint-disable-next-line @next/next/no-location-assign-relative-destination -- Auth0 routes require a full document navigation.
  globalThis.location.href = "/auth/logout";
}

/**
 * Signals that the user's session has expired and they need to re-authenticate.
 *
 * Unlike {@link clearCachesAndLogout}, this does NOT immediately redirect.
 * Instead it dispatches a DOM event (`gail:session-expired`) that the
 * {@link SessionTimeoutProvider} listens for, showing a warning modal with
 * a grace countdown before the automatic logout.
 *
 * Use this instead of `clearCachesAndLogout` in background token refresh
 * paths (auto-refresh hook, reactive 401 retry) to avoid a jarring instant
 * redirect.
 */
export function notifySessionExpired(): void {
  if (globalThis.window === undefined) return;

  // Persistent latch — set before the cooldown gate so repeat calls within
  // the cooldown window still keep refresh dispatchers short-circuited.
  _sessionExpired = true;

  // Siblings will fall back to discovering the dead session via their own 401
  // if the broadcast can't be written.
  broadcast(SESSION_EXPIRED_CHANNEL, String(Date.now()));

  // Only fire once per cooldown window — multiple concurrent 401s should not
  // dispatch duplicate events. The flag auto-resets after 30s so a later
  // genuine 401 can still surface the modal.
  if (_sessionExpiredNotified) return;
  _sessionExpiredNotified = true;

  if (_sessionExpiredResetTimer) {
    clearTimeout(_sessionExpiredResetTimer);
  }
  _sessionExpiredResetTimer = setTimeout(() => {
    _sessionExpiredNotified = false;
    _sessionExpiredResetTimer = null;
  }, SESSION_EXPIRED_NOTIFY_COOLDOWN_MS);

  if (_hasSessionExpiredListener) {
    globalThis.window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
  } else {
    // No SessionTimeoutProvider mounted — fall back to direct logout
    // so the user doesn't get stuck with a dead session and no modal.
    clearCachesAndLogout({ saveReturnUrl: true });
  }
}
